import {
  CsvFileUploadImportConfig,
  FileUploadImportConfig,
  LocalImportConfig,
  WebhookImportConfig,
} from '@oneschema/react';
import { Company } from '~/types/company';
import { Consent, ConsentStatus } from '~/types/consent';
import { BookkeepingAccount } from '~/types/general-bookkeeping';
import { MetaDataCursorPage } from '~/types/jsonapi';
import { Money } from '~/types/money';

export interface Account {
  id: string;
  currentBalance?: Money | null;
  availableBalance?: Money | null;
  creditLimit?: Money | null;
  currentBalanceEur?: number | null;
  availableBalanceEur?: number | null;
  balanceDate: string | null;
  createdAt: Date;
  currency: string;
  hiddenAt: Date | null;
  nickname?: string;
  status: AccountStatus;
  consent: Consent;
  lastUpdatedAt: string | null;
  ignoreBalance: boolean | null;
  identifiers: AccountIdentifier[];
  bookkeepingAccount: BookkeepingAccount | null;
  allowBalanceChange: boolean;
  cashbackAgreedAt?: Date;
  sepaInitiatingPartyIdentifier?: string;
  manualAccount?: Account | null;
  linkedBankAccounts?: Account[];
}

export interface AccountWithConsent extends Account {
  consent: Consent;
}

export interface AccountIdentifier {
  type:
    | 'ABA'
    | 'ABA_ACH'
    | 'ABA_WIRE'
    | 'ACCOUNT_NUMBER'
    | 'BBAN'
    | 'BIC'
    | 'BLZ'
    | 'BRANCH_CODE'
    | 'BSB'
    | 'CLABE'
    | 'ACCOUNT_HOLDER'
    | 'CTN'
    | 'EMAIL'
    | 'IBAN'
    | 'IFS'
    | 'MASKED_PAN'
    | 'MSISDN'
    | 'NCC'
    | 'PAN'
    | 'ROLL_NUMBER'
    | 'SORT_CODE'
    | 'VIRTUAL_ACCOUNT_ID'
    | 'WALLET';
  value: string;
}

export enum AccountStatus {
  ACTIVE = 'ACTIVE',
  ERROR = 'ERROR',
  CONFLICTING = 'CONFLICTING',
}

export enum AccountDeleteReason {
  REMOVE_DATA = 'remove_data',
  REMOVE_ACCOUNT = 'remove_account',
  UPDATE = 'update',
  OTHER = 'other',
}

export const AccountResourceType = 'account';

export const AccountQueryKeys = {
  all: ['Accounts'],
  company: (companyId: Company['id'], filters?: AccountsFilters) => ['Accounts', companyId, filters],
  balances: (companyId: Company['id'], filters: AccountFilters) => ['Accounts', companyId, 'Balances', filters],
  importTemplates: (companyId: Company['id'], accountId: Account['id']) => [
    'Accounts',
    companyId,
    'ImportTemplates',
    accountId,
  ],
};

export interface BalanceData {
  available?: Money | null;
  current?: Money | null;
  count: number;
  updatedAt?: Date | null;
}

export interface AccountMeta {
  page: MetaDataCursorPage;
}

export interface AccountUpdate {
  id: string;
  ignoreBalance?: boolean;
  nickname?: string;
  balance?: Money;
  hiddenAt?: Date | null;
  bookkeepingAccountId?: string | null;
  linkedBankAccountIds?: string[];
}

export interface AccountFilters {
  companyId?: Company['id'];
  idsIn?: string[];
  ignoreBalance?: boolean;
}

export interface AccountsFilters {
  ids?: string[];
  consentStatuses?: ConsentStatus[];
  manual?: boolean;
}

export interface AccountCreateRequestPayload {
  nickname: string;
  balance: Money;
  company: {
    id: string;
  };
  consent: {
    id: string;
  };
  linkedBankAccountIds?: string[];
}

export interface AccountImportTemplate {
  id: string;
  name: string;
  description: string;
}

export interface AccountImportConfig {
  baseUrl: string;
  clientId: string;
  templateKey: string;
  importConfig: LocalImportConfig | WebhookImportConfig | CsvFileUploadImportConfig | FileUploadImportConfig;
  userJwt: string;
  devMode: boolean;
}

export interface AccountImportTransactionsPayload {
  config: {
    templateKey: string;
    userJwt: string;
  };
  data: unknown;
}
